import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { genUID, ListboxProvider, PopoverDialog, useFormField, usePopoverDialog } from '@frontend/design-system';
import { ListBoxPopover } from '../list-box-popover';
import { DefaultViewConfig, PrimaryViewConfig } from '../list-box-popover/types';
import { CustomInput } from './custom-input';

export type Option = {
  id: string;
  label: string;
};

type Props = {
  disabled?: boolean;
  error?: string;
  showAvatar?: boolean;
  isMultiSelect?: boolean;
  searchPlaceholder?: string;
  defaultViewConfig: DefaultViewConfig;
  primaryViewConfig?: PrimaryViewConfig;
  value?: Option[];
  leftIconName?: IconName;
  onChange: (val: string | string[]) => void;
  onRemove: (val: string) => void;
  onSearch: (val: string) => void;
};

export const MultiViewCombobox = ({
  disabled = false,
  error,
  showAvatar,
  isMultiSelect = false,
  searchPlaceholder,
  defaultViewConfig,
  primaryViewConfig,
  value,
  leftIconName,
  onChange,
  onRemove,
  onSearch,
}: Props) => {
  const { t } = useTranslation('schedule');
  const labelId = useMemo(() => genUID(), []);
  const searchFieldProps = useFormField({ type: 'text', placeholder: searchPlaceholder || t('Search') });

  const { getTriggerProps, getDialogProps, close } = usePopoverDialog<HTMLElement>({
    placement: 'bottom',
    initialOffset: { x: 0, y: -10 },
  });

  const handleSelection = (val: string | string[]) => {
    onChange(val);
    // Close the popover dialog on selection for single select
    if (!isMultiSelect) {
      close();
    }
  };

  const handleRemove = (val: string) => {
    onRemove(val);
  };

  // Call `onSearch` callback when search term changes
  useEffect(() => {
    onSearch(searchFieldProps.value);
  }, [searchFieldProps.value, onSearch]);

  return (
    <ListboxProvider
      labelId={labelId}
      value={(isMultiSelect ? value?.map(({ id }) => id) : value?.[0]?.id) || ''}
      onSelect={handleSelection}
    >
      <CustomInput
        disabled={disabled}
        placeholder={searchFieldProps.placeholder}
        showAvatar={showAvatar}
        error={error}
        value={value}
        onRemove={handleRemove}
        getTriggerProps={getTriggerProps}
        leftIconName={leftIconName}
      />
      <PopoverDialog {...getDialogProps()} css={popoverStyles}>
        <ListBoxPopover
          searchFieldProps={searchFieldProps}
          defaultViewConfig={{ ...defaultViewConfig, onCancel: close }}
          primaryViewConfig={primaryViewConfig}
          onClose={close}
        />
      </PopoverDialog>
    </ListboxProvider>
  );
};

const popoverStyles = css({
  padding: 0,
  display: 'flex',
  borderRadius: theme.borderRadius.large,
});
